// Request signer.
//
// The hashcash proof-of-work — the "single-use param you can't reproduce with a
// one-line curl" — runs in WebAssembly (assembly/signer.ts): a tight synchronous
// SHA-256 loop at native speed, over a small request-bound challenge.
//
// HMAC-SHA256 (the request signature) and plain SHA-256 (the ciphertext-binding
// hash) use native WebCrypto: async, correct for any input size, and byte-exact
// with the server (Python hmac/hashlib). Doing the signature/hash in WASM would
// mean marshalling arbitrary-length data through a fixed linear-memory buffer,
// which is error-prone; the PoW is the part that benefits from WASM, so that's
// the only thing kept there.

import { SIGNER_WASM_BASE64 } from './signerWasm';

interface SignerExports {
  memory: WebAssembly.Memory;
  pow(chalLen: number, difficulty: number, start: number, maxIter: number): number;
  chalOffset(): number;
}

const POW_BATCH = 1 << 22; // iterations per wasm call before we bump the start
const POW_MAX = 1 << 27; // overall cap so a bad difficulty can't hang the tab

let signerPromise: Promise<SignerExports> | null = null;
const encoder = new TextEncoder();

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function load(): Promise<SignerExports> {
  const module = await WebAssembly.compile(base64ToBytes(SIGNER_WASM_BASE64));
  const instance = await WebAssembly.instantiate(module, {
    env: {
      abort() {
        throw new Error('signer wasm aborted');
      },
    },
  });
  return instance.exports as unknown as SignerExports;
}

function getSigner(): Promise<SignerExports> {
  if (!signerPromise) signerPromise = load();
  return signerPromise;
}

function bytesToHex(bytes: Uint8Array): string {
  let hex = '';
  for (let i = 0; i < bytes.length; i++) hex += bytes[i]!.toString(16).padStart(2, '0');
  return hex;
}

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.trim().toLowerCase();
  const out = new Uint8Array(new ArrayBuffer(clean.length >> 1));
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
  return out;
}

/** HMAC-SHA256(keyHex, canonical) as lowercase hex (native WebCrypto). */
export async function signRequest(canonical: string, keyHex: string): Promise<string> {
  const key = await crypto.subtle.importKey(
    'raw',
    hexToBytes(keyHex),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, encoder.encode(canonical));
  return bytesToHex(new Uint8Array(sig));
}

/** SHA-256 hex of a UTF-8 string (native WebCrypto). */
export async function sha256Hex(message: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', encoder.encode(message));
  return bytesToHex(new Uint8Array(digest));
}

/**
 * Find a counter such that SHA-256(`challenge`:`counter`) has at least
 * `difficulty` leading zero bits. Runs the search loop in WASM. Returns the
 * counter as a decimal string (matches what the server appends and re-hashes).
 */
export async function solvePow(challenge: string, difficulty: number): Promise<string> {
  const s = await getSigner();
  const mem = new Uint8Array(s.memory.buffer);
  const chal = encoder.encode(challenge);
  mem.set(chal, s.chalOffset());
  for (let start = 0; start < POW_MAX; start += POW_BATCH) {
    const found = s.pow(chal.length, difficulty, start, POW_BATCH);
    if (found >= 0) return String(found >>> 0);
  }
  throw new Error(`proof-of-work not found within ${POW_MAX} iterations (difficulty ${difficulty})`);
}
